<template>
  <TransitionRoot :show="open" as="template" @after-leave="query = ''" appear>
    <Dialog class="relative z-50" @close="emit('update:open', false)">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500/25 transition-opacity dark:bg-gray-900/50" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="mx-auto max-w-xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl outline outline-1 outline-black/5 transition-all dark:divide-white/10 dark:bg-gray-900 dark:-outline-offset-1 dark:outline-white/10"
          >
            <Combobox @update:modelValue="onSelect">
              <div class="grid grid-cols-1">
                <ComboboxInput
                  class="col-start-1 row-start-1 h-12 w-full border-0 bg-transparent pl-11 pr-4 text-base text-gray-900 outline-none placeholder:text-gray-400 focus:ring-0 sm:text-sm dark:text-white dark:placeholder:text-gray-500"
                  placeholder="Search nodes..."
                  @change="query = $event.target.value"
                />
                <MagnifyingGlassIcon
                  class="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-gray-400 dark:text-gray-500"
                  aria-hidden="true"
                />
              </div>

              <ComboboxOptions
                v-if="filteredItems.length > 0"
                static
                class="max-h-96 transform-gpu scroll-py-3 overflow-y-auto p-3"
              >
                <ComboboxOption
                  v-for="item in filteredItems"
                  :key="item.uuid"
                  :value="item"
                  as="template"
                  v-slot="{ active }"
                >
                  <li
                    :class="[
                      'flex cursor-default select-none rounded-xl p-3',
                      active && 'bg-gray-100 outline-none dark:bg-white/5'
                    ]"
                  >
                    <div
                      :class="[
                        'flex size-10 flex-none items-center justify-center rounded-lg',
                        item.color
                      ]"
                    >
                      <iconify-icon
                        :icon="item.icon"
                        class="size-6 text-white"
                        aria-hidden="true"
                      />
                    </div>
                    <div class="ml-4 flex-auto overflow-hidden">
                      <p
                        :class="[
                          'truncate text-sm font-medium',
                          active
                            ? 'text-gray-900 dark:text-white'
                            : 'text-gray-700 dark:text-gray-200'
                        ]"
                      >
                        {{ item.name }}
                      </p>
                      <p
                        :class="[
                          'truncate text-sm',
                          active
                            ? 'text-gray-700 dark:text-gray-300'
                            : 'text-gray-500 dark:text-gray-400'
                        ]"
                      >
                        {{ item.description }}
                      </p>
                    </div>
                  </li>
                </ComboboxOption>
              </ComboboxOptions>

              <div
                v-if="query !== '' && filteredItems.length === 0"
                class="px-6 py-14 text-center text-sm sm:px-14"
              >
                <ExclamationCircleIcon
                  class="mx-auto size-6 text-gray-400 dark:text-gray-500"
                />
                <p class="mt-4 font-semibold text-gray-900 dark:text-white">
                  No results found
                </p>
                <p class="mt-2 text-gray-500 dark:text-gray-400">
                  No nodes found matching "{{ query }}". Try a different search term.
                </p>
              </div>
            </Combobox>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid'
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'

/**
 * FlowSearch Component
 *
 * A command palette-style search dialog for finding and navigating to nodes
 * within a flow. Searches the visible DOM content of rendered nodes.
 *
 * Keyboard shortcuts: 's' or '/' opens this dialog
 * ESC or clicking outside closes it
 */

interface SearchResult {
  uuid: string
  name: string
  description: string
  icon: string
  color: string
}

interface Props {
  open: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'select-node': [uuid: string]
}>()

const query = ref('')

// Icon mapping by node type (detected from iconify-icon attribute)
const iconMap: Record<string, string> = {
  'hugeicons:message-question': 'hugeicons:message-question',
  'hugeicons:flow-square': 'hugeicons:flow-square',
  'hugeicons:hierarchy-square-08': 'hugeicons:hierarchy-square-08',
  'hugeicons:award-01': 'hugeicons:award-01',
  'hugeicons:inbox': 'hugeicons:inbox',
  'hugeicons:gift': 'hugeicons:gift',
  'hugeicons:message-01': 'hugeicons:message-01',
  'hugeicons:move-right': 'hugeicons:move-right',
  'hugeicons:code': 'hugeicons:code',
  'hugeicons:database': 'hugeicons:database',
  'hugeicons:filter-horizontal': 'hugeicons:filter-horizontal',
  'hugeicons:logout-04': 'hugeicons:logout-04'
}

// Color mapping by icon type
const colorMap: Record<string, string> = {
  'hugeicons:message-question': 'bg-amber-500', // Question split
  'hugeicons:flow-square': 'bg-fuchsia-500', // Router split
  'hugeicons:hierarchy-square-08': 'bg-fuchsia-500', // Random split
  'hugeicons:award-01': 'bg-purple-500', // High score
  'hugeicons:inbox': 'bg-cyan-500', // Form offer
  'hugeicons:gift': 'bg-cyan-500', // Promotion offer
  'hugeicons:message-01': 'bg-teal-500', // Message
  'hugeicons:move-right': 'bg-orange-500', // Redirect
  'hugeicons:code': 'bg-slate-500', // Code
  'hugeicons:database': 'bg-indigo-500', // Data
  'hugeicons:filter-horizontal': 'bg-emerald-500', // YesNo condition
  'hugeicons:logout-04': 'bg-gray-500' // Exit
}

// Default fallback
const defaultIcon = 'hugeicons:help-circle'
const defaultColor = 'bg-gray-500'

/**
 * Scans the DOM for all flow nodes and extracts searchable information
 */
function scanNodes(): SearchResult[] {
  // Query all nodes excluding those in the minimap
  const nodeElements = document.querySelectorAll(
    '.flowbuilder-node:not(.flow-map .flowbuilder-node)'
  )

  const results: SearchResult[] = []

  nodeElements.forEach((el) => {
    const uuid = el.getAttribute('data-node-id')
    if (!uuid) return

    // Skip widget/trigger nodes (they're inside .flowbuilder-widgets)
    if (el.closest('.flowbuilder-widgets')) return

    // Get the iconify-icon element to determine node type
    const iconEl = el.querySelector('iconify-icon')
    const iconName = iconEl?.getAttribute('icon') || defaultIcon

    // Get the context text (e.g., "Ask a question:", "Condition check:")
    // This is in the slot named "context" which renders in a div with specific classes
    const contextEl = el.querySelector('.flex.items-center.gap-x-1\\.5')
    const contextText = contextEl?.textContent?.trim() || ''

    // Get the main content - everything else in the node
    // Clone the element to avoid modifying the original DOM
    const clone = el.cloneNode(true) as HTMLElement

    // Remove elements we don't want in the search text
    clone.querySelectorAll('.flowbuilder-split-label').forEach((e) => e.remove())
    clone.querySelectorAll('.flowbuilder-splits').forEach((e) => e.remove())
    clone.querySelectorAll('[class*="absolute"]').forEach((e) => e.remove()) // Remove badges, context labels

    // Remove metrics elements (displays, clicks, conversion rate, etc.)
    clone.querySelectorAll('[class*="bg-slate-300"]').forEach((e) => e.remove()) // Metrics container
    clone.querySelectorAll('.user-count-amount').forEach((e) => e.remove()) // Visitor counts
    clone.querySelectorAll('.flowbuilder-split-user-count').forEach((e) => e.remove()) // Split user counts

    // Get remaining text content
    const fullText = clone.textContent?.trim() || ''

    // Parse the text to extract name and description
    // The context text is part of fullText, so we remove it to get the actual content
    let mainContent = fullText
    if (contextText && mainContent.startsWith(contextText)) {
      mainContent = mainContent.slice(contextText.length).trim()
    }

    // Clean up the text - remove excessive whitespace
    mainContent = mainContent.replace(/\s+/g, ' ').trim()

    // First line or sentence becomes the name, rest is description
    const lines = mainContent.split(/[.\n]/).filter((l) => l.trim())
    const name = lines[0]?.trim() || contextText || 'Unnamed Node'
    const description = contextText || getNodeTypeLabel(iconName)

    results.push({
      uuid,
      name: truncate(name, 60),
      description,
      icon: iconMap[iconName] || defaultIcon,
      color: colorMap[iconName] || defaultColor
    })
  })

  return results
}

/**
 * Get a human-readable label for a node type based on its icon
 */
function getNodeTypeLabel(icon: string): string {
  const labels: Record<string, string> = {
    'hugeicons:message-question': 'Question',
    'hugeicons:flow-square': 'Route by segment',
    'hugeicons:hierarchy-square-08': 'Random split',
    'hugeicons:award-01': 'High score',
    'hugeicons:inbox': 'Form',
    'hugeicons:gift': 'Offer',
    'hugeicons:message-01': 'Message',
    'hugeicons:move-right': 'Redirect',
    'hugeicons:code': 'Code',
    'hugeicons:database': 'Set data',
    'hugeicons:filter-horizontal': 'Condition',
    'hugeicons:logout-04': 'Exit'
  }
  return labels[icon] || 'Node'
}

/**
 * Truncate text to a maximum length
 */
function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

// All nodes in the flow (refreshed when dialog opens)
const allNodes = ref<SearchResult[]>([])

// Refresh nodes when dialog opens
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        allNodes.value = scanNodes()
      }, 50)
    }
  },
  { immediate: true }
)

// Filtered nodes based on search query (shows all when empty, filters when typing)
const filteredItems = computed(() => {
  if (query.value === '') return allNodes.value

  const searchTerm = query.value.toLowerCase()
  return allNodes.value.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm) ||
      item.description.toLowerCase().includes(searchTerm)
  )
})

/**
 * Handle node selection from the combobox
 */
function onSelect(item: SearchResult | null) {
  if (item) {
    emit('select-node', item.uuid)
    emit('update:open', false)
  }
}
</script>
